// /home/Hakyury/sites/oauth.eight-percent.com/frontend/src/component/OAuthHome.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getTokens, setTokens, clearTokens } from "../utils/authFetch";


// Компонент для уведомлений
const Notification = ({ type, message, onClose }) => {
  useEffect(() => {
    if (type !== 'loading') {
      const timer = setTimeout(() => {
        onClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [type, onClose]);

  const bgColor = {
    success: 'bg-green-500/20 border-green-500/30',
    error: 'bg-red-500/20 border-red-500/30',
    warning: 'bg-yellow-500/20 border-yellow-500/30',
    info: 'bg-blue-500/20 border-blue-500/30',
    loading: 'bg-purple-500/20 border-purple-500/30'
  }[type];

  const textColor = {
    success: 'text-green-300',
    error: 'text-red-300',
    warning: 'text-yellow-300',
    info: 'text-blue-300',
    loading: 'text-purple-300'
  }[type];

  const icons = {
    success: '✅',
    error: '❌',
    warning: '⚠️',
    info: 'ℹ️',
    loading: '⏳'
  }[type];

  return (
    <div className={`fixed top-20 right-4 z-50 ${bgColor} border rounded-lg p-4 min-w-80 max-w-md backdrop-blur-sm transition-all duration-300`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <span className="text-lg">{icons}</span>
          <div>
            <p className={`font-medium ${textColor}`}>
              {type === 'success' && 'Успешно!'}
              {type === 'error' && 'Ошибка!'}
              {type === 'warning' && 'Внимание!'}
              {type === 'info' && 'Информация'}
              {type === 'loading' && 'Загрузка...'}
            </p>
            <p className="text-white text-sm mt-1">{message}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white ml-4"
        >
          ✕
        </button>
      </div>
      {type === 'loading' && (
        <div className="mt-2 h-1 w-full bg-gray-700 rounded-full overflow-hidden">
          <div className="h-full bg-purple-500 animate-pulse"></div>
        </div>
      )}
    </div>
  );
};

export default function OAuthHome() {
  const [isLogin, setIsLogin] = useState(true);
  const [showAgreement, setShowAgreement] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [notification, setNotification] = useState(null);
  const [registeredSuccess, setRegisteredSuccess] = useState(false);
  const location = useLocation();

  // Разрешаем возврат только на сайты экосистемы. Иначе токены можно было бы
  // передать на произвольный внешний адрес через параметр next.
  const getRedirectUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    const nextParam = searchParams.get('next');

    const fallbackUrl = 'https://eight-percent.com';
    if (!nextParam) return fallbackUrl;

    try {
      const decodedUrl = decodeURIComponent(nextParam);
      const url = new URL(decodedUrl);
      const allowedOrigins = new Set([
        'https://eight-percent.com',
        'https://campfire.eight-percent.com',
        'https://etoile.eight-percent.com',
        'https://frameparty.eight-percent.com',
        'https://profile.eight-percent.com',
        'https://admin.eight-percent.com',
      ]);

      return url.protocol === 'https:' && allowedOrigins.has(url.origin)
        ? url.toString()
        : fallbackUrl;
    } catch (error) {
      console.error('Ошибка декодирования URL:', error);
      return fallbackUrl;
    }
  };

  const redirectUrl = getRedirectUrl();
  const isLogoutRequest = new URLSearchParams(location.search).get('logout') === '1';

  // Функция для показа уведомлений
  const showNotification = useCallback((type, message) => {
    setNotification({ type, message });
  }, []);

  // Функция для скрытия уведомления
  const hideNotification = useCallback(() => {
    setNotification(null);
  }, []);

  // Обработчик успешной регистрации
  const handleRegisterSuccess = useCallback((message = 'Регистрация успешна! Письмо с подтверждением отправлено на ваш email.') => {
    showNotification('success', message);
    setRegisteredSuccess(true);
    // Переключаем на форму входа через 3 секунды
    setTimeout(() => {
      setIsLogin(true);
      setRegisteredSuccess(false);
    }, 3000);
  }, [showNotification]);

  // Обработчик успешной авторизации
  const handleAuthSuccess = (data) => {
    // Сохраняем токены
    setTokens({ access: data.access, refresh: data.refresh });
    
    showNotification('success', 'Авторизация успешна! Перенаправление...');
    
    // Создаем безопасный редирект
    try {
      const redirectUrl = getRedirectUrl();
      
      // Проверяем, что URL безопасен
      if (redirectUrl && redirectUrl.startsWith('http')) {
        // Плавный переход
        setTimeout(() => {
          // Отправляем сообщение через postMessage вместо токенов в URL
          if (window.opener) {
            window.opener.postMessage({
              type: 'oauth_success',
              access_token: data.access,
              refresh_token: data.refresh,
              user: {
                login: data.login,
                role: data.role
              }
            }, new URL(redirectUrl).origin);
            window.close();
          } else {
            // Если нет opener, используем URL с токенами (запасной вариант)
            const url = new URL(redirectUrl);
            url.searchParams.append('access_token', data.access);
            url.searchParams.append('refresh_token', data.refresh);
            window.location.href = url.toString();
          }
        }, 1500);
      } else {
        // Если URL невалидный, редирект на основной домен
        window.location.href = 'https://eight-percent.com';
      }
    } catch (error) {
      console.error('Ошибка редиректа:', error);
      window.location.href = 'https://eight-percent.com';
    }
  };

  // Эффект звездного неба
  useEffect(() => {
    const createStars = () => {
      const container = document.getElementById('oauth-starfield');
      if (!container) return;
      
      container.innerHTML = '';
      
      for (let i = 0; i < 80; i++) {
        const star = document.createElement('div');
        star.className = 'star';
        star.style.cssText = `
          position: absolute;
          width: ${Math.random() * 2 + 0.5}px;
          height: ${Math.random() * 2 + 0.5}px;
          background: rgba(255, 255, 255, ${Math.random() * 0.4 + 0.1});
          border-radius: 50%;
          left: ${Math.random() * 100}%;
          top: ${Math.random() * 100}%;
          animation: twinkle ${Math.random() * 3 + 1.5}s infinite alternate;
          z-index: 0;
        `;
        container.appendChild(star);
      }
    };
    
    createStars();
    window.addEventListener('resize', createStars);
    return () => window.removeEventListener('resize', createStars);
  }, []);

  // Эффект движения мыши для туманности
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ 
        x: e.clientX - window.innerWidth / 2, 
        y: e.clientY - window.innerHeight / 2 
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Явный выход приходит с другого домена. Сначала очищаем OAuth-токены в
  // localStorage этого домена, затем возвращаем пользователя на разрешённый next.
  useEffect(() => {
    if (!isLogoutRequest) return;

    clearTokens();
    window.location.replace(redirectUrl);
  }, [isLogoutRequest, redirectUrl]);

  useEffect(() => {
    if (isLogoutRequest) return;

    const checkAuthAndRedirect = () => {
      const { access } = getTokens(); // из authFetch oauth
      const next = redirectUrl;

      if (access) {
        // Пользователь уже авторизован – редиректим на next с токенами
        const redirectUrl = new URL(next);
        redirectUrl.searchParams.append('access_token', access);
        redirectUrl.searchParams.append('refresh_token', getTokens().refresh);
        window.location.href = redirectUrl.toString();
      }
    };

    checkAuthAndRedirect();
  }, [isLogoutRequest, redirectUrl]);

  return (
    <div className="relative min-h-screen bg-black overflow-hidden">
      {/* Звездное небо */}
      <div id="oauth-starfield" className="fixed inset-0 pointer-events-none z-0"></div>
      
      {/* Параллакс туманности */}
      <div className="fixed inset-0 overflow-hidden z-0">
        {/* Основная туманность с параллаксом */}
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full blur-3xl opacity-20"
          style={{
            transform: `translate(calc(-50% + ${mousePosition.x * 0.02}px), calc(-50% + ${mousePosition.y * 0.02}px))`,
            background: 'radial-gradient(circle, rgba(147,51,234,0.3) 0%, rgba(79,70,229,0.2) 30%, transparent 60%)',
          }}
        ></div>
        
        {/* Вторая туманность с противоположным параллаксом */}
        <div 
          className="absolute top-1/4 right-1/4 w-[600px] h-[600px] rounded-full blur-3xl opacity-10"
          style={{
            transform: `translate(${mousePosition.x * -0.01}px, ${mousePosition.y * -0.01}px)`,
            background: 'radial-gradient(circle, rgba(56,189,248,0.2) 0%, transparent 50%)',
          }}
        ></div>
        
        {/* Третья туманность */}
        <div 
          className="absolute bottom-1/4 left-1/4 w-[500px] h-[500px] rounded-full blur-3xl opacity-10"
          style={{
            transform: `translate(${mousePosition.x * 0.015}px, ${mousePosition.y * 0.015}px)`,
            background: 'radial-gradient(circle, rgba(236,72,153,0.15) 0%, transparent 50%)',
          }}
        ></div>
      </div>

      {/* Уведомления */}
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={hideNotification}
        />
      )}

      {/* Основной контент */}
      <main className="relative z-10 pt-24 pb-12">
        <div className="max-w-md mx-auto px-4">
          {/* Логотип с анимацией */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-600 via-indigo-600 to-cyan-500 flex items-center justify-center border border-white/20 shadow-xl shadow-purple-500/20">
                <span className="text-white font-bold text-2xl">8%</span>
              </div>
              <div className="absolute -inset-4 border border-purple-400/30 rounded-full animate-ping opacity-50"></div>
              <div className="absolute -inset-2 border border-cyan-400/20 rounded-full animate-pulse"></div>
              
              {/* Вращающиеся орбиты */}
              <div className="absolute -inset-6 border border-purple-500/10 rounded-full animate-orbit"></div>
              <div className="absolute -inset-8 border border-cyan-500/10 rounded-full animate-orbit-reverse"></div>
            </div>
          </div>

          {/* Карточка формы с эффектами */}
          <div className="relative group">
            {/* Свечение вокруг карточки */}
            <div className="absolute -inset-4 bg-gradient-to-r from-purple-600/20 via-indigo-600/20 to-cyan-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            <div className="relative bg-gray-900/70 backdrop-blur-xl border border-white/10 rounded-xl p-6 shadow-2xl">
              {/* Парящие частицы внутри карточки */}
              <div className="absolute inset-0 overflow-hidden rounded-xl">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-1 h-1 bg-white/10 rounded-full animate-float"
                    style={{
                      left: `${10 + i * 20}%`,
                      top: `${15 + i * 10}%`,
                      animationDelay: `${i * 0.5}s`,
                    }}
                  ></div>
                ))}
              </div>

              <div className="relative z-10">
                {registeredSuccess ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mx-auto mb-4 border border-white/20 shadow-lg shadow-green-500/20">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-green-400 mb-2">Регистрация успешна!</h3>
                    <p className="text-gray-300 mb-4">
                      Письмо с подтверждением отправлено на ваш email.
                      Для активации аккаунта перейдите по ссылке в письме.
                    </p>
                    <div className="mt-6">
                      <button
                        onClick={() => setIsLogin(true)}
                        className="py-3 px-6 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                      >
                        Перейти к входу
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex space-x-2 mb-6">
                      <button
                        onClick={() => setIsLogin(true)}
                        className={`flex-1 py-3 rounded-xl font-medium transition-all duration-300 ${
                          isLogin 
                            ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-500/25' 
                            : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-800/70'
                        }`}
                      >
                        Вход
                      </button>
                      <button
                        onClick={() => setIsLogin(false)}
                        className={`flex-1 py-3 rounded-xl font-medium transition-all duration-300 ${
                          !isLogin 
                            ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-500/25' 
                            : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-800/70'
                        }`}
                      >
                        Регистрация
                      </button>
                    </div>

                    {isLogin ? (
                      <LoginForm 
                        onSwitch={() => setIsLogin(false)} 
                        onSuccess={handleAuthSuccess}
                        redirectUrl={redirectUrl}
                        showNotification={showNotification}
                      />
                    ) : (
                      <RegisterForm 
                        onSwitch={() => setIsLogin(true)}
                        onShowAgreement={() => setShowAgreement(true)}
                        agreed={agreed}
                        onRegisterSuccess={handleRegisterSuccess}
                        redirectUrl={redirectUrl}
                        showNotification={showNotification}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Информация о сервисе */}
          <div className="mt-8 text-center">
            <p className="text-gray-400 text-sm mb-2">Eight Percent OAuth — единая система аутентификации</p>
            <p className="text-gray-500 text-xs">Один аккаунт для всех проектов</p>
            <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-blue-400 text-sm">
                После авторизации вы будете перенаправлены на:<br/>
                <span className="text-xs break-all">{redirectUrl}</span>
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Модальное окно соглашения с космическим стилем */}
      {showAgreement && (
        <AgreementModal
          onClose={() => setShowAgreement(false)}
          onAgree={() => {
            setAgreed(true);
            setShowAgreement(false);
          }}
        />
      )}

      {/* Добавляем CSS анимации */}
      <style>{`
        @keyframes orbit {
          0% { transform: rotate(0deg) translateX(0px) rotate(0deg); }
          100% { transform: rotate(360deg) translateX(0px) rotate(-360deg); }
        }
        
        @keyframes orbit-reverse {
          0% { transform: rotate(0deg) translateX(0px) rotate(0deg); }
          100% { transform: rotate(-360deg) translateX(0px) rotate(360deg); }
        }
        
        .animate-orbit {
          animation: orbit 15s linear infinite;
        }
        
        .animate-orbit-reverse {
          animation: orbit-reverse 20s linear infinite;
        }
        
        @keyframes float {
          0%, 100% { 
            transform: translateY(0) translateX(0);
            opacity: 0.1;
          }
          25% { 
            transform: translateY(-10px) translateX(5px);
            opacity: 0.3;
          }
          50% { 
            transform: translateY(-5px) translateX(-5px);
            opacity: 0.2;
          }
          75% { 
            transform: translateY(5px) translateX(3px);
            opacity: 0.4;
          }
        }
        
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

// Компонент формы входа
function LoginForm({ onSwitch, onSuccess, redirectUrl, showNotification }) {
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [focused, setFocused] = useState({ username: false, password: false });

  // Функция для получения CSRF токена из куки
  const getCSRFToken = () => {
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrftoken='))
      ?.split('=')[1];
    return cookieValue || '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const csrfToken = getCSRFToken();
      
      const response = await fetch('/api/accounts/token/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify({
          login: form.username,
          password: form.password
        }),
        credentials: 'include', // Важно для CORS
      });
      
      const data = await response.json();
      
      if (response.ok) {
        onSuccess(data);
      } else {
        const errorMsg = data.detail || data.error || 'Неверный логин или пароль';
        setError(errorMsg);
        showNotification('error', errorMsg);
      }
    } catch (err) {
      const errorMsg = 'Ошибка соединения с сервером. Проверьте подключение к интернету.';
      setError(errorMsg);
      showNotification('error', errorMsg);
      console.error('Ошибка при входе:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}
      
      <div className="relative">
        <input
          type="text"
          placeholder="Логин"
          className="w-full px-4 py-3 bg-gray-800/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all duration-300"
          value={form.username}
          onChange={(e) => setForm({...form, username: e.target.value})}
          onFocus={() => setFocused({...focused, username: true})}
          onBlur={() => setFocused({...focused, username: false})}
        />
        {focused.username && (
          <div className="absolute bottom-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-purple-500 to-transparent"></div>
        )}
      </div>
      
      <div className="relative">
        <input
          type="password"
          placeholder="Пароль"
          className="w-full px-4 py-3 bg-gray-800/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all duration-300"
          value={form.password}
          onChange={(e) => setForm({...form, password: e.target.value})}
          onFocus={() => setFocused({...focused, password: true})}
          onBlur={() => setFocused({...focused, password: false})}
        />
        {focused.password && (
          <div className="absolute bottom-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-purple-500 to-transparent"></div>
        )}
      </div>
      
      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/25"
      >
        {loading ? (
          <span className="flex items-center justify-center">
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></span>
            Вход...
          </span>
        ) : 'Войти'}
      </button>

      <div className="text-center pt-4 border-t border-white/10">
        <button
          type="button"
          onClick={onSwitch}
          className="text-gray-400 hover:text-white text-sm transition-colors"
        >
          Нет аккаунта? <span className="text-purple-400 hover:text-purple-300">Зарегистрироваться</span>
        </button>
      </div>
    </form>
  );
}

// Компонент формы регистрации
function RegisterForm({ onSwitch, onShowAgreement, agreed, onRegisterSuccess, redirectUrl, showNotification }) {
  const [form, setForm] = useState({
    email: '',
    username: '',
    password: '',
    confirm: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState({
    email: false,
    username: false,
    password: false,
    confirm: false
  });
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [emailAvailable, setEmailAvailable] = useState(null);

  // Функция для получения CSRF токена из куки
  const getCSRFToken = () => {
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrftoken='))
      ?.split('=')[1];
    return cookieValue || '';
  };

  // Валидация логина: только латиница, цифры и символы
  const validateLogin = (login) => {
    const regex = /^[a-zA-Z0-9!@#$%^&*()_+\-=\x5B\x5D{};':"\\|,.<>/?]*$/;
    return regex.test(login);
  };

  // Проверка доступности логина
  const checkUsername = async (username) => {
    if (username.length < 3) {
      setUsernameAvailable(null);
      return;
    }
    
    if (!validateLogin(username)) {
      setUsernameAvailable(false);
      setErrors(prev => ({ ...prev, username: 'Только английские буквы, цифры и символы' }));
      return;
    }
    
    setCheckingUsername(true);
    try {
      const response = await fetch('/api/accounts/check-username/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ login: username }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setUsernameAvailable(data.available);
        if (!data.available) {
          setErrors(prev => ({ ...prev, username: 'Логин уже занят' }));
        } else {
          setErrors(prev => ({ ...prev, username: '' }));
        }
      }
    } catch (error) {
      console.error('Ошибка проверки логина:', error);
    } finally {
      setCheckingUsername(false);
    }
  };

  // Проверка доступности email
  const checkEmail = async (email) => {
    if (!email.includes('@')) {
      setEmailAvailable(null);
      return;
    }
    
    setCheckingEmail(true);
    try {
      const response = await fetch('/api/accounts/check-email/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setEmailAvailable(data.available);
        if (!data.available) {
          setErrors(prev => ({ ...prev, email: 'Email уже зарегистрирован' }));
        } else {
          setErrors(prev => ({ ...prev, email: '' }));
        }
      }
    } catch (error) {
      console.error('Ошибка проверки email:', error);
    } finally {
      setCheckingEmail(false);
    }
  };

  // Общая валидация формы перед отправкой
  const validateForm = () => {
    const newErrors = {};

    if (!form.username.trim()) {
      newErrors.username = 'Логин обязателен';
    } else if (!validateLogin(form.username)) {
      newErrors.username = 'Только английские буквы, цифры и символы';
    } else if (form.username.length < 3) {
      newErrors.username = 'Логин должен быть не менее 3 символов';
    } else if (usernameAvailable === false) {
      newErrors.username = 'Логин уже занят';
    }

    if (!form.email.trim()) {
      newErrors.email = 'Email обязателен';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Некорректный email';
    } else if (emailAvailable === false) {
      newErrors.email = 'Email уже зарегистрирован';
    }

    if (form.password.length < 8) {
      newErrors.password = 'Минимум 8 символов';
    }

    if (form.password !== form.confirm) {
      newErrors.confirm = 'Пароли не совпадают';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!agreed) {
      showNotification('warning', 'Для регистрации необходимо принять условия соглашения');
      onShowAgreement();
      return;
    }

    // Проверяем всю форму
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    const csrfToken = getCSRFToken();

    try {
      // 1. Регистрация
      const regResponse = await fetch('/api/accounts/registration/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify({
          login: form.username,
          email: form.email,
          password1: form.password,
          password2: form.confirm
        }),
        credentials: 'include',
      });

      const regData = await regResponse.json();
      
      if (!regResponse.ok) {
        // Обработка ошибок от сервера
        if (regData.errors) {
          const serverErrors = {};
          if (regData.errors.email) serverErrors.email = regData.errors.email;
          if (regData.errors.login) serverErrors.username = regData.errors.login;
          if (regData.errors.password1) serverErrors.password = regData.errors.password1;
          setErrors(serverErrors);
          
          // Показываем первую ошибку в уведомлении
          const firstError = Object.values(serverErrors)[0];
          if (firstError) {
            showNotification('error', firstError);
          }
        } else if (regData.detail) {
          setErrors({ general: regData.detail });
          showNotification('error', regData.detail);
        } else {
          setErrors({ general: 'Ошибка регистрации' });
          showNotification('error', 'Ошибка регистрации');
        }
        return;
      }

      // Успешная регистрация
      const successMessage = regData.detail || 'Регистрация успешна! Письмо с подтверждением отправлено на ваш email.';
      onRegisterSuccess(successMessage);
      
      // Очищаем форму
      setForm({
        email: '',
        username: '',
        password: '',
        confirm: '',
      });
      setUsernameAvailable(null);
      setEmailAvailable(null);
      
    } catch (err) {
      console.error('Ошибка при регистрации:', err);
      const errorMsg = 'Ошибка соединения с сервером. Проверьте подключение к интернету.';
      setErrors({ general: errorMsg });
      showNotification('error', errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    
    // Очищаем ошибку для этого поля при вводе
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (errors.general) {
      setErrors(prev => ({ ...prev, general: '' }));
    }
    
    // Проверяем доступность в реальном времени
    if (field === 'username') {
      if (value.length >= 3) {
        checkUsername(value);
      } else {
        setUsernameAvailable(null);
      }
    } else if (field === 'email' && value.includes('@')) {
      checkEmail(value);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Общая ошибка */}
      {errors.general && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400 text-sm">{errors.general}</p>
        </div>
      )}
      
      {/* Поле Email с индикатором проверки */}
      <div className="relative">
        <div className="flex items-center space-x-2 mb-1">
          <input
            type="email"
            placeholder="Email"
            className={`flex-1 px-4 py-3 bg-gray-800/50 border rounded-lg text-white placeholder-gray-500 focus:outline-none transition-all duration-300 ${
              errors.email 
                ? 'border-red-500/50 focus:ring-2 focus:ring-red-500/20' 
                : focused.email 
                  ? 'border-purple-500/50 focus:ring-2 focus:ring-purple-500/20'
                  : 'border-white/10 hover:border-white/20'
            }`}
            value={form.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            onFocus={() => setFocused(prev => ({ ...prev, email: true }))}
            onBlur={() => setFocused(prev => ({ ...prev, email: false }))}
          />
          {checkingEmail ? (
            <div className="w-8 h-8 flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
            </div>
          ) : emailAvailable !== null && (
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${
              emailAvailable ? 'bg-green-500/20' : 'bg-red-500/20'
            }`}>
              {emailAvailable ? (
                <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
          )}
        </div>
        {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
        {emailAvailable && !errors.email && (
          <p className="text-green-400 text-sm mt-1">✓ Email доступен</p>
        )}
      </div>

      {/* Поле Логин с индикатором проверки */}
      <div className="relative">
        <div className="flex items-center space-x-2 mb-1">
          <input
            type="text"
            placeholder="Логин (только латиница, мин. 3 символа)"
            className={`flex-1 px-4 py-3 bg-gray-800/50 border rounded-lg text-white placeholder-gray-500 focus:outline-none transition-all duration-300 ${
              errors.username 
                ? 'border-red-500/50 focus:ring-2 focus:ring-red-500/20' 
                : focused.username 
                  ? 'border-purple-500/50 focus:ring-2 focus:ring-purple-500/20'
                  : 'border-white/10 hover:border-white/20'
            }`}
            value={form.username}
            onChange={(e) => {
              const value = e.target.value;
              if (validateLogin(value) || value === '') {
                handleInputChange('username', value);
              }
            }}
            onFocus={() => setFocused(prev => ({ ...prev, username: true }))}
            onBlur={() => setFocused(prev => ({ ...prev, username: false }))}
          />
          {checkingUsername ? (
            <div className="w-8 h-8 flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
            </div>
          ) : usernameAvailable !== null && (
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${
              usernameAvailable ? 'bg-green-500/20' : 'bg-red-500/20'
            }`}>
              {usernameAvailable ? (
                <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
          )}
        </div>
        {errors.username && <p className="text-red-400 text-sm mt-1">{errors.username}</p>}
        {usernameAvailable && !errors.username && (
          <p className="text-green-400 text-sm mt-1">✓ Логин доступен</p>
        )}
      </div>

      {/* Поле Пароль */}
      <div className="relative">
        <input
          type="password"
          placeholder="Пароль (мин. 8 символов)"
          className={`w-full px-4 py-3 bg-gray-800/50 border rounded-lg text-white placeholder-gray-500 focus:outline-none transition-all duration-300 ${
            errors.password 
              ? 'border-red-500/50 focus:ring-2 focus:ring-red-500/20' 
              : focused.password 
                ? 'border-purple-500/50 focus:ring-2 focus:ring-purple-500/20'
                : 'border-white/10 hover:border-white/20'
          }`}
          value={form.password}
          onChange={(e) => handleInputChange('password', e.target.value)}
          onFocus={() => setFocused(prev => ({ ...prev, password: true }))}
          onBlur={() => setFocused(prev => ({ ...prev, password: false }))}
        />
        {errors.password && <p className="text-red-400 text-sm mt-1">{errors.password}</p>}
      </div>

      {/* Поле Подтверждения пароля */}
      <div className="relative">
        <input
          type="password"
          placeholder="Повторите пароль"
          className={`w-full px-4 py-3 bg-gray-800/50 border rounded-lg text-white placeholder-gray-500 focus:outline-none transition-all duration-300 ${
            errors.confirm 
              ? 'border-red-500/50 focus:ring-2 focus:ring-red-500/20' 
              : focused.confirm 
                ? 'border-purple-500/50 focus:ring-2 focus:ring-purple-500/20'
                : 'border-white/10 hover:border-white/20'
          }`}
          value={form.confirm}
          onChange={(e) => handleInputChange('confirm', e.target.value)}
          onFocus={() => setFocused(prev => ({ ...prev, confirm: true }))}
          onBlur={() => setFocused(prev => ({ ...prev, confirm: false }))}
        />
        {errors.confirm && <p className="text-red-400 text-sm mt-1">{errors.confirm}</p>}
        {form.confirm && form.password === form.confirm && !errors.confirm && (
          <p className="text-green-400 text-sm mt-1">✓ Пароли совпадают</p>
        )}
      </div>

      <div className="flex items-center space-x-3 p-3 bg-gray-800/30 rounded-lg border border-white/5">
        <div className="relative">
          <input
            type="checkbox"
            id="agreement"
            checked={agreed}
            onChange={() => {}}
            onClick={onShowAgreement}
            className="sr-only"
          />
          <label 
            htmlFor="agreement"
            className={`w-5 h-5 rounded border flex items-center justify-center cursor-pointer transition-all duration-300 ${
              agreed 
                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 border-purple-500' 
                : 'bg-gray-800/50 border-white/20 hover:border-white/40'
            }`}
          >
            {agreed && (
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </label>
        </div>
        <span className="text-sm text-gray-300">
          Согласен с{' '}
          <button
            type="button"
            onClick={onShowAgreement}
            className="text-purple-400 hover:text-purple-300 transition-colors"
          >
            условиями
          </button>
        </span>
      </div>

      <button
        type="submit"
        disabled={loading || !agreed}
        className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/25"
      >
        {loading ? (
          <span className="flex items-center justify-center">
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></span>
            Регистрация...
          </span>
        ) : 'Создать аккаунт'}
      </button>

      <div className="text-center pt-4 border-t border-white/10">
        <button
          type="button"
          onClick={onSwitch}
          className="text-gray-400 hover:text-white text-sm transition-colors"
        >
          Уже есть аккаунт? <span className="text-purple-400 hover:text-purple-300">Войти</span>
        </button>
      </div>
    </form>
  );
}

// Модальное окно соглашения (остается без изменений)
function AgreementModal({ onClose, onAgree }) {
  const [scrolled, setScrolled] = useState(false);
  const contentRef = useRef(null);

  const handleScroll = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      setScrolled(scrollTop + clientHeight >= scrollHeight - 10);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      {/* Фоновые эффекты */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/10 via-black/50 to-indigo-900/10"></div>
      
      <div className="relative bg-gray-900/90 backdrop-blur-xl border border-white/10 rounded-2xl max-w-lg w-full max-h-[80vh] flex flex-col shadow-2xl shadow-purple-500/10">
        {/* Шапка модалки */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-white">Пользовательское соглашение</h3>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Контент с прокруткой */}
        <div 
          ref={contentRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto p-6 space-y-4"
        >
          <div className="text-gray-300 space-y-4">
            <p className="leading-relaxed">
              Eight Percent OAuth — это единая система аутентификации, разработанная для безопасного и удобного доступа ко всем проектам Eight Percent.
            </p>
            
            <div>
              <h4 className="text-purple-400 font-medium mb-2">1. Собираемые данные</h4>
              <p className="text-gray-400">
                Для предоставления услуг мы обрабатываем следующие данные:
              </p>
              <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-400">
                <li>Адрес электронной почты (email)</li>
                <li>Имя пользователя (логин)</li>
                <li>Хэшированный пароль</li>
                <li>Технические данные (IP-адрес, тип устройства)</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-purple-400 font-medium mb-2">2. Цель обработки</h4>
              <p className="text-gray-400">
                Ваши данные используются исключительно для:
              </p>
              <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-400">
                <li>Аутентификации и авторизации во всех проектах Eight Percent</li>
                <li>Обеспечения безопасности вашего аккаунта</li>
                <li>Предоставления персонализированного опыта</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-purple-400 font-medium mb-2">3. Конфиденциальность</h4>
              <p className="text-gray-400">
                Мы не передаем ваши данные третьим лицам. Все данные хранятся на защищенных серверах с использованием современных методов шифрования.
              </p>
            </div>
            
            <div>
              <h4 className="text-purple-400 font-medium mb-2">4. Контакты</h4>
              <p className="text-gray-400">
                По всем вопросам вы можете обратиться:
              </p>
              <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-400">
                <li>Техническая поддержка: admin@eight-percent.com</li>
                <li>Общие вопросы: info@eight-percent.com</li>
                <li>Запросы на сотрудничество: request@eight-percent.com</li>
              </ul>
            </div>
            
            <div className="pt-4 border-t border-white/10">
              <p className="text-sm text-gray-500 text-center">
                Прокрутите до конца для принятия соглашения
              </p>
            </div>
          </div>
        </div>
        
        {/* Футер модалки */}
        <div className="p-6 border-t border-white/10 bg-gray-900/50">
          <div className="flex justify-between items-center">
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-lg bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-800/70 border border-white/10 transition-all duration-300"
            >
              Отмена
            </button>
            <button
              onClick={onAgree}
              disabled={!scrolled}
              className={`px-6 py-2.5 rounded-lg font-medium transition-all duration-300 ${
                scrolled
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-purple-500/25'
                  : 'bg-gray-800 text-gray-500 cursor-not-allowed'
              }`}
            >
              Принимаю условия
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
